import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle, AlertCircle, Loader2, Circle } from 'lucide-react';
import { usersService } from '@/services/users';
import { getApiErrorMessage } from '@/utils/apiError';
import { BaytarcLogo } from '@/components/brand/BaytarcLogo';

const PASSWORD_RULES = [
  { label: 'At least 8 characters', test: (p: string) => p.length >= 8 },
  { label: 'One uppercase letter (A–Z)', test: (p: string) => /[A-Z]/.test(p) },
  { label: 'One lowercase letter (a–z)', test: (p: string) => /[a-z]/.test(p) },
  { label: 'One number (0–9)', test: (p: string) => /\d/.test(p) },
  {
    label: 'One special character (!@#$%…)',
    test: (p: string) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(p),
  },
];

const AcceptInvite = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const [inviteDetails, setInviteDetails] = useState<{ email: string; role: string } | null>(null);
  const [loadingInvite, setLoadingInvite] = useState(true);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [existingAccount, setExistingAccount] = useState(false);

  useEffect(() => {
    if (!token) {
      setInviteError('No invite token found in the link. Please use the link from your email.');
      setLoadingInvite(false);
      return;
    }

    usersService
      .getInviteDetails(token)
      .then((details) => {
        setInviteDetails(details);
        setLoadingInvite(false);
      })
      .catch((err) => {
        setInviteError(getApiErrorMessage(err, 'This invite link is invalid or has expired.'));
        setLoadingInvite(false);
      });
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    const failing = PASSWORD_RULES.filter((r) => !r.test(password));
    if (failing.length > 0) {
      setSubmitError(`Password requirements not met: ${failing.map((r) => r.label).join(', ')}.`);
      return;
    }
    if (password !== confirmPassword) {
      setSubmitError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      const result = (await usersService.acceptInvite(token, { password })) as {
        existingAccount?: boolean;
      };
      setExistingAccount(!!result?.existingAccount);
      setDone(true);
    } catch (err) {
      setSubmitError(getApiErrorMessage(err, 'Failed to accept invite. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary-50 to-secondary-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8 flex flex-col items-center">
          <BaytarcLogo variant="navy" size="lg" />
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Loading */}
          {loadingInvite && (
            <div className="flex flex-col items-center py-8 gap-3 text-secondary-500">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
              <p className="text-sm">Validating your invite…</p>
            </div>
          )}

          {/* Invalid invite */}
          {!loadingInvite && inviteError && (
            <div className="flex flex-col items-center py-6 gap-4 text-center">
              <AlertCircle className="w-12 h-12 text-error-500" />
              <h2 className="text-lg font-semibold text-secondary-900">Invalid Invite</h2>
              <p className="text-sm text-secondary-600">{inviteError}</p>
              <p className="text-sm text-secondary-400">
                Your administrator can resend the invitation from the Users page.
              </p>
              <Link
                to="/login"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Back to login
              </Link>
            </div>
          )}

          {/* Success */}
          {done && (
            <div className="flex flex-col items-center py-6 gap-4 text-center">
              <CheckCircle className="w-12 h-12 text-success-500" />
              {existingAccount ? (
                <>
                  <h2 className="text-lg font-semibold text-secondary-900">Application linked!</h2>
                  <p className="text-sm text-secondary-600">
                    This application has been linked to your existing account. Log in with your
                    existing email and password to continue.
                  </p>
                </>
              ) : (
                <>
                  <h2 className="text-lg font-semibold text-secondary-900">Account created!</h2>
                  <p className="text-sm text-secondary-600">
                    Your account has been set up. You can now log in with your email and password.
                  </p>
                </>
              )}
              <button
                onClick={() => navigate('/login')}
                className="w-full py-2.5 px-4 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 transition-colors"
              >
                Go to login
              </button>
            </div>
          )}

          {/* Accept form */}
          {!loadingInvite && !inviteError && !done && inviteDetails && (
            <>
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-secondary-900 mb-1">
                  Accept your invite
                </h2>
                <p className="text-sm text-secondary-600">
                  You've been invited as{' '}
                  <span className="font-medium text-secondary-800">{inviteDetails.role}</span> for{' '}
                  <span className="font-medium text-secondary-800">{inviteDetails.email}</span>. Set
                  a password to get started.
                </p>
              </div>

              {submitError && (
                <div className="mb-4 flex items-start gap-2 p-3 bg-error-50 border border-error-200 rounded-xl text-sm text-error-700">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  {submitError}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1.5">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Min. 8 chars, uppercase, number, special char"
                      required
                      className="input pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Password requirements checklist */}
                {password.length > 0 && (
                  <ul className="space-y-1 mt-2">
                    {PASSWORD_RULES.map((rule) => {
                      const ok = rule.test(password);
                      return (
                        <li
                          key={rule.label}
                          className={`flex items-center gap-2 text-xs ${ok ? 'text-success-600' : 'text-secondary-400'}`}
                        >
                          {ok ? (
                            <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />
                          ) : (
                            <Circle className="w-3.5 h-3.5 flex-shrink-0" />
                          )}
                          {rule.label}
                        </li>
                      );
                    })}
                  </ul>
                )}

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1.5">
                    Confirm password
                  </label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your password"
                    required
                    className="input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-2.5 px-4 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {submitting ? 'Creating account…' : 'Create account'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AcceptInvite;
